// ---------------------------------------------------------------------------
// useInputUpload — hook unificado de upload/replicate/reprocess de input V3.
// ---------------------------------------------------------------------------
// Onda 2.1 (mai/2026): otimismo de status do input REMOVIDO. O fluxo
// passou a ser:
//
//   1. setIsUploading(true)               -> spinner visivel no botao
//   2. inputsState.pause()                -> aborta fetch em voo +
//                                             para o polling (sem isso,
//                                             um fetch disparado ANTES
//                                             do click chegava DEPOIS
//                                             do POST com estado pre-
//                                             acao e sobrescrevia a UI
//                                             com dado obsoleto)
//   3. POST ao backend (await)            -> backend faz reset
//                                             sincrono e atomico
//   4. inputsState.resume({refreshNow})   -> libera polling + dispara
//                                             refetch imediato; payload
//                                             ja vem coerente com a
//                                             acao executada
//   5. cleanup helper (idempotente)       -> setIsUploading(false)
//   6. notify success/fail
//
// Spinner ``isUploading`` cobre o intervalo cliclo-completo (~200-500ms);
// durante esse periodo, a UI fica congelada no estado pre-click — sem
// piscar para um estado errado, sem otimismo, sem refs de "input em
// processamento" para sincronizar entre cockpit e drawer.
//
// Returns:
//   {
//     isUploading: bool,
//     uploadFile: (metaName, file) => void,
//     replicate: (metaName) => void,
//     reprocess: (metaName) => void,
//   }
// ---------------------------------------------------------------------------

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { client } from "../../../../services/axiosClient";
import {
  uploadInputV3,
  replicateInputV3,
  reprocessInputV3,
} from "../../../../services/inputsV3Service";
import { store } from "../../../../store";
import { setProgress } from "../../../../reducers/fileTransferReducer";

const TR_UPLOAD_DONE = "modeling_inputs_v2.upload_completed";
const TR_ERROR_PREFIX = "modeling_inputs_v2.errors.";

export function useInputUpload({
  appNameRef,
  selectedMonthData,
  inputDrawerInput,
  inputsStatePause,
  inputsStateResume,
  notify,
  userRole,
}) {
  const { t } = useTranslation();
  const [busyAction, setBusyAction] = useState(null);
  const isUploading = busyAction !== null;

  // Suprime toast de sucesso quando o drawer do MESMO input esta aberto
  // (a timeline do NowPane ja comunica o recebimento). Disparos
  // externos ao drawer continuam notificando normalmente.
  const shouldNotifySuccess = useCallback(
    (metaName) =>
      !inputDrawerInput || inputDrawerInput.input_control_id !== metaName,
    [inputDrawerInput],
  );

  // Cleanup roda em TODOS os caminhos de saida (success/fail/throw).
  // Idempotente. Sempre retoma o polling com refresh imediato — o
  // payload pos-acao traz o veredicto real do backend.
  const buildCleanup = useCallback(
    ({ resetProgress = false } = {}) =>
      () => {
        const finish = () => {
          setBusyAction(null);
          if (resetProgress) {
            store.dispatch(setProgress(0));
          }
        };
        if (typeof inputsStateResume === "function") {
          return Promise.resolve(
            inputsStateResume({ refreshNow: true }),
          ).finally(finish);
        }
        finish();
        return Promise.resolve(null);
      },
    [inputsStateResume],
  );

  // -------------------------------------------------------------------
  // uploadFile — caminho com awake + multipart upload
  // -------------------------------------------------------------------
  const uploadFile = useCallback(
    (metaName, file) => {
      const appName = appNameRef.current;
      if (!appName || !selectedMonthData || !file) {
        notify(t(TR_ERROR_PREFIX + "missing_file"), "error", 5000);
        return;
      }

      store.dispatch(setProgress(0));
      setBusyAction("upload");
      if (typeof inputsStatePause === "function") inputsStatePause();

      const cleanup = buildCleanup({ resetProgress: true });

      client
        .post("api/awake/")
        .then(() =>
          uploadInputV3({
            appName,
            year: selectedMonthData.year,
            month: selectedMonthData.month,
            metaName,
            file,
          }),
        )
        .then((data) => {
          if (data?.success) {
            if (shouldNotifySuccess(metaName)) {
              notify(t(TR_UPLOAD_DONE), "success");
            }
          } else {
            notify(t(TR_ERROR_PREFIX + data?.error_code), "error", 5000);
          }
        })
        .catch((error) => {
          const errorCode = error?.response?.data?.error_code;
          if (errorCode) {
            notify(t(TR_ERROR_PREFIX + errorCode), "error", 5000);
          } else {
            console.log(error);
          }
        })
        .finally(() => {
          cleanup();
        });
    },
    [
      appNameRef,
      selectedMonthData,
      inputsStatePause,
      buildCleanup,
      shouldNotifySuccess,
      notify,
      t,
    ],
  );

  // -------------------------------------------------------------------
  // replicate — copia o arquivo do periodo anterior
  // -------------------------------------------------------------------
  const replicate = useCallback(
    (metaName) => {
      const appName = appNameRef.current;
      if (!metaName || !appName || !selectedMonthData) return;

      setBusyAction("replicate");
      if (typeof inputsStatePause === "function") inputsStatePause();

      const cleanup = buildCleanup();

      replicateInputV3({
        appName,
        year: selectedMonthData.year,
        month: selectedMonthData.month,
        metaName,
      })
        .then((data) => {
          if (data?.success) {
            if (shouldNotifySuccess(metaName)) {
              notify(t(TR_UPLOAD_DONE), "success");
            }
          } else {
            notify(t(TR_ERROR_PREFIX + data?.error_code), "error", 5000);
          }
        })
        .catch((error) => {
          const errorCode = error?.response?.data?.error_code;
          if (errorCode) {
            notify(t(TR_ERROR_PREFIX + errorCode), "error", 5000);
          } else {
            console.log(error);
          }
        })
        .finally(() => {
          cleanup();
        });
    },
    [
      appNameRef,
      selectedMonthData,
      inputsStatePause,
      buildCleanup,
      shouldNotifySuccess,
      notify,
      t,
    ],
  );

  // -------------------------------------------------------------------
  // reprocess — DEV-only, re-executa parse + xlinks + blocos
  // -------------------------------------------------------------------
  const reprocess = useCallback(
    (metaName) => {
      const appName = appNameRef.current;
      if (!metaName || !appName || !selectedMonthData) {
        return Promise.resolve(null);
      }
      if (userRole !== "DEV") return Promise.resolve(null);

      setBusyAction("reprocess");
      if (typeof inputsStatePause === "function") inputsStatePause();

      const cleanup = buildCleanup();

      return reprocessInputV3({
        appName,
        year: selectedMonthData.year,
        month: selectedMonthData.month,
        metaName,
      })
        .then((data) => {
          if (!data?.success) {
            const code = data?.error_code;
            throw new Error(code || "reprocess_failed");
          }
          // Rev. 2026-05-26: toast de "Reprocessamento iniciado..."
          // removido a pedido do consultor. O feedback visual ja
          // acontece via:
          //   - ``busyAction='reprocess'`` ativa o overlay listrado +
          //     o card "Reprocessando arquivo existente" no NowPane;
          //   - ``invalidate_dependents`` simulado no FE limpa pips
          //     de xlinks/blocos contraparte imediatamente.
          // Mantemos apenas o toast de erro (catch abaixo) — ali o
          // usuario precisa saber que a acao falhou porque o estado
          // visual nao muda em caso de falha sincrona.
        })
        .catch((error) => {
          const code = error?.response?.data?.error_code;
          notify(
            code || error?.message
              ? t(TR_ERROR_PREFIX + (code || error.message))
              : "Falha ao reprocessar.",
            "error",
            5000,
          );
          throw error;
        })
        .finally(() => cleanup());
    },
    [
      appNameRef,
      selectedMonthData,
      userRole,
      inputsStatePause,
      buildCleanup,
      notify,
      t,
    ],
  );

  return {
    isUploading,
    busyAction,
    uploadFile,
    replicate,
    reprocess,
  };
}
